import { chooseSpatialTarget, isGamepadFocusable, listFocusables, type Direction } from './gamepad';

export type OverviewStep = {
  click?: HTMLElement | null;
  focus?: HTMLElement | null;
  /** Nach einem Klick den Fokus neu setzen, weil React die Liste neu sortiert. */
  follow?: 'roster' | 'zone';
  /** Kachel merken, falls der Knopf nach Reparieren oder Benutzen verschwindet. */
  anchor?: SheetAnchor | null;
};

export type SheetAnchor = {
  zone: string;
  key: string;
  index: number;
  action: string;
};

function rectOf(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

export function rosterRows(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-roster-row]'))
    .filter((row) => isGamepadFocusable(row));
}

export function selectedRosterRow(root: ParentNode): HTMLElement | null {
  return rosterRows(root).find((row) => row.getAttribute('aria-pressed') === 'true') ?? null;
}

export function mergeRosterCycle(cycle: number[], ids: number[]): number[] {
  const known = new Set(ids);
  const kept = cycle.filter((id) => known.has(id));
  const added = ids.filter((id) => !kept.includes(id));
  return [...kept, ...added];
}

/** Dreht die Liste so, dass der Gewählte oben steht. Alle davor rutschen ans Ende. */
export function rotateRosterTo(ids: number[], selectedId: number): number[] {
  const index = ids.indexOf(selectedId);
  if (index <= 0) return ids;
  return [...ids.slice(index), ...ids.slice(0, index)];
}

export function nextRosterRow(root: ParentNode, delta: 1 | -1): HTMLElement | null {
  const rows = rosterRows(root);
  if (rows.length === 0) return null;
  const current = selectedRosterRow(root) ?? rows[0];
  const index = Math.max(0, rows.indexOf(current));
  return rows[(index + delta + rows.length) % rows.length];
}

function zoneHasContent(zone: HTMLElement): boolean {
  if (zone.querySelector('[data-gamepad-tile]')) return true;
  return listFocusables(zone).some((element) => element !== zone && !element.closest('[data-gamepad-tile]'));
}

export function sheetZones(root: ParentNode): HTMLElement[] {
  const sheet = root.querySelector('[data-gamepad-region="sheet"]');
  if (!(sheet instanceof HTMLElement)) return [];
  return Array.from(sheet.querySelectorAll<HTMLElement>('[data-gamepad-zone]'))
    .filter((zone) => isGamepadFocusable(zone) && zoneHasContent(zone));
}

/** Stick-Ziele im Bogen: Aktions- und Vitalbereich, sonst die einzelnen Kacheln. */
export function sheetStops(root: ParentNode): HTMLElement[] {
  const stops: HTMLElement[] = [];
  for (const zone of sheetZones(root)) {
    const tiles = tilesIn(zone);
    if (tiles.length > 0) stops.push(...tiles);
    else stops.push(zone);
  }
  return stops;
}

function tilesIn(zone: HTMLElement): HTMLElement[] {
  return Array.from(zone.querySelectorAll<HTMLElement>('[data-gamepad-tile]'))
    .filter((tile) => isGamepadFocusable(tile));
}

function controlsIn(scope: HTMLElement): HTMLElement[] {
  return listFocusables(scope).filter((element) => (
    element !== scope
    && !element.hasAttribute('data-gamepad-zone')
    && !element.hasAttribute('data-gamepad-tile')
    && !element.closest('[data-gamepad-tile]')
  ));
}

export type OverviewLevel = 'roster' | 'zone' | 'tile' | 'control' | 'other';

export function overviewLevel(focus: HTMLElement): OverviewLevel {
  if (focus.matches('[data-gamepad-roster-row]')) return 'roster';
  if (focus.matches('[data-gamepad-zone]')) return 'zone';
  if (focus.matches('[data-gamepad-tile]')) return 'tile';
  if (focus.closest('[data-gamepad-tile]')) return 'control';
  if (focus.closest('[data-gamepad-zone]')) return 'control';
  return 'other';
}

function stepAmong(focus: HTMLElement, peers: HTMLElement[], direction: Direction): HTMLElement | null {
  const others = peers.filter((peer) => peer !== focus);
  const candidates = others.map((element, index) => ({
    id: String(index),
    rect: rectOf(element),
    containsCurrent: false,
    insideCurrent: false,
  }));
  const spatial = chooseSpatialTarget(rectOf(focus), candidates, direction);
  if (spatial != null) return others[Number(spatial)] ?? null;
  const index = peers.indexOf(focus);
  if (index < 0) return peers[0] ?? null;
  const delta = direction === 'down' || direction === 'right' ? 1 : -1;
  const next = index + delta;
  if (next < 0 || next >= peers.length) return null;
  return peers[next];
}

export function overviewMoveTarget(root: ParentNode, focus: HTMLElement, direction: Direction): OverviewStep | null {
  const level = overviewLevel(focus);
  if (level === 'roster' && (direction === 'up' || direction === 'down')) {
    return { click: nextRosterRow(root, direction === 'down' ? 1 : -1), follow: 'roster' };
  }
  if (level === 'zone' || level === 'tile') {
    return { focus: stepAmong(focus, sheetStops(root), direction) };
  }
  if (level === 'control') {
    const tile = focus.closest('[data-gamepad-tile]');
    const scope = tile instanceof HTMLElement ? tile : focus.closest('[data-gamepad-zone]');
    if (!(scope instanceof HTMLElement)) return null;
    const peers = tile instanceof HTMLElement
      ? listFocusables(scope).filter((element) => element !== scope)
      : controlsIn(scope);
    return { focus: stepAmong(focus, peers, direction) };
  }
  return null;
}

function firstInside(zone: HTMLElement): HTMLElement | null {
  return tilesIn(zone)[0] ?? controlsIn(zone)[0] ?? null;
}

export function sheetAnchorFrom(focus: HTMLElement): SheetAnchor | null {
  const action = focus.getAttribute('data-gamepad-action');
  if (!action) return null;
  const tile = focus.closest('[data-gamepad-tile]');
  const zone = focus.closest('[data-gamepad-zone]');
  if (!(tile instanceof HTMLElement) || !(zone instanceof HTMLElement)) return null;
  const tiles = tilesIn(zone);
  return {
    zone: zone.getAttribute('data-gamepad-zone') || '',
    key: tile.getAttribute('data-gamepad-tile-key') || '',
    index: Math.max(0, tiles.indexOf(tile)),
    action,
  };
}

/** Gleiche Kachel, sonst die nächste in derselben Zone. Nicht zurück zur Charakterliste. */
export function focusAfterSheetAction(root: ParentNode, anchor: SheetAnchor): HTMLElement | null {
  const zone = Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-zone]'))
    .find((entry) => entry.getAttribute('data-gamepad-zone') === anchor.zone);
  if (!(zone instanceof HTMLElement)) return null;
  const tiles = tilesIn(zone);
  const same = anchor.key
    ? tiles.find((tile) => tile.getAttribute('data-gamepad-tile-key') === anchor.key) ?? null
    : null;
  if (same) {
    const control = Array.from(same.querySelectorAll<HTMLElement>('[data-gamepad-action]'))
      .find((entry) => entry.getAttribute('data-gamepad-action') === anchor.action && isGamepadFocusable(entry));
    return control ?? same;
  }
  if (tiles.length === 0) return isGamepadFocusable(zone) ? zone : null;
  return tiles[anchor.index] ?? tiles[anchor.index - 1] ?? tiles[0];
}

export function overviewActivate(root: ParentNode, focus: HTMLElement | null): OverviewStep {
  if (!focus) return { focus: selectedRosterRow(root) ?? rosterRows(root)[0] ?? null };
  const level = overviewLevel(focus);
  if (level === 'roster') {
    const row = focus.matches('[data-gamepad-roster-row]') ? focus : selectedRosterRow(root);
    return {
      click: row && row.getAttribute('aria-pressed') !== 'true' ? row : null,
      follow: 'zone',
    };
  }
  if (level === 'zone') return { focus: firstInside(focus) };
  if (level === 'tile') return { focus: listFocusables(focus)[0] ?? null };
  return { click: focus, anchor: sheetAnchorFrom(focus) };
}

export function overviewBack(root: ParentNode, focus: HTMLElement): HTMLElement | null {
  const level = overviewLevel(focus);
  if (level === 'control') {
    const tile = focus.closest('[data-gamepad-tile]');
    if (tile instanceof HTMLElement) return tile;
    const zone = focus.closest('[data-gamepad-zone]');
    return zone instanceof HTMLElement ? zone : null;
  }
  if (level === 'tile') return selectedRosterRow(root);
  if (level === 'zone') return selectedRosterRow(root);
  return null;
}
